import React, { useState, useRef, useEffect } from "react";
import GeoSearch from "../Map/GeoSearch";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import IGeoPosition from "./models/IGeoPosition";
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from '../../utilities/automation';
import { APIProvider } from "@vis.gl/react-google-maps";
import useLanguage from '../../utilities/useLanguage';
import googleConfig from "../Map/google-config";

declare var google;

export interface IAdvisorSearchProps {
    defaultRadius?: number;
    defaultValue?: string;
    focusOnInput?: boolean;
    userLocation?: IGeoPosition,
    loadMap: boolean;
    onSearch?: (lat: number, lng: number, radius: number, address?: string) => void;
    onChange?: (inputValue: string, radius: number) => void;
}

const SearchColumn = styled.div`
    min-height: 54px;

    @media (min-width: 992px) {
        width: 250px;
    }

    .form-control{
        background: ${props => props.theme.gray250};
    }

    .input-text {
        margin: 0 !important;
        height: auto !important;
    }
`;

const SearchRow = styled.div`
    background: white;
`;

export default ({ userLocation, loadMap = false, onSearch, onChange, focusOnInput, defaultValue, defaultRadius = 25 }: IAdvisorSearchProps) => {
    const { lang } = useLanguage();
    const [doSearch, setDoSearch] = useState(false);
    const [lng, setLng] = useState<number|null>();
    const [lat, setLat] = useState<number|null>();
    const [inputValue, setInputValue] = useState("");
    const [place, setPlace] = useState(null);
    const [radius, setRadius] = useState(defaultRadius);
    const [cityZipClasses, setCityZipClasses] = useState('');

    const serviceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const autocompleteRef = useRef(null);
    const geocodeRef = useRef(null);

    async function loadInputValueFromUserLocation() {
        if (userLocation && geocodeRef.current) {
            try {
                const cityState = await getCityFromLatLng(userLocation);

                if (cityState) {
                    setInputValue(cityState);
                    setLat(userLocation.lat);
                    setLng(userLocation.lng);
                }
            } catch (err) {
                console.error(err)
            }
        }
    }

    useEffect(() => {
        setInputValue(defaultValue);
        setRadius(defaultRadius);
    }, [defaultValue, defaultRadius]);

    useEffect(() => {
        //any time there is a change also change but dont update the URL address
        if (inputValue !== "" && radius) {
            if (typeof onChange === "function") {
                onChange(inputValue, radius);
            }
        }

    }, [inputValue, radius]);

    useEffect(() => {
        loadInputValueFromUserLocation();
    }, [userLocation])

    useEffect(() => {
        async function getLocations() {
            let placeSearch = place;

            if (!placeSearch) {
                placeSearch = await loadLatLngFromUrlParams(inputValue);
                setPlace(placeSearch);
            }

            const address = placeSearch.formatted_address;
            const latitude = placeSearch.geometry.location.lat();
            const longitude = placeSearch.geometry.location.lng();

            setInputValue(address);
            setLat(latitude);
            setLng(longitude);

            if (typeof onSearch === "function") {
                onSearch(latitude, longitude, radius, address);
            }
        }

        if (doSearch) {
            if (typeof onSearch === "function") {
                getLocations();
            }
            setDoSearch(false);
        }
    }, [doSearch, inputValue, lat, lng, radius]);

    function getPlaceFromQuery(query): Promise<any> {
        if (serviceRef.current) {
            return new Promise((resolve) => {
                serviceRef.current.getPlacePredictions({
                    input: query,
                    types: ["(regions)"],
                    componentRestrictions: { country: "us" }
                }, function (results) {
                    resolve(results);
                });
            });
        } else {
            console.error("Autocomplete service has not loaded yet");
        }
    }

    function getCityFromLatLng(query): Promise<any> {
        let cityState;

        if (serviceRef.current) {
            return new Promise((resolve) => {
                geocodeRef.current.geocode({
                    location: query
                }, function (results) {
                    if (results.length) {
                        for (var i = 0; i < results.length; i++) {
                            if (results[i].types[0] === "locality") {
                                cityState = results[i].formatted_address;
                            };
                        };
                    }
                    resolve(cityState);
                });
            });
        } else {
            console.error("Geolocation service has not loaded yet");
        }
    }

    async function getPlaceDetails(id) {
        if (placesServiceRef.current) {
            return new Promise((resolve) => {
                placesServiceRef.current.getDetails({
                    placeId: id
                }, function (results) {
                    resolve(results);
                });
            });
        } else {
            console.error("Place service has not loaded yet");
        }
    }

    async function loadLatLngFromUrlParams(query): Promise<any> {
        const results = await getPlaceFromQuery(query);

        if (results && results.length > 0) {
            return await getPlaceDetails(results[0]['place_id']);
        }
    }

    async function handleLoad(autocomplete) {
        autocompleteRef.current = autocomplete;
        geocodeRef.current = new google.maps.Geocoder();
        serviceRef.current = new google.maps.places.AutocompleteService();
        placesServiceRef.current = new google.maps.places.PlacesService(document.createElement('div'));

        loadInputValueFromUserLocation();

        if (defaultValue !== "") {
            setInputValue(defaultValue);
            setDoSearch(true);
        }
    }

    async function handlePlaceChanged(place) {
        if (autocompleteRef.current) {
            if (place && place.geometry) {
                const address = place.formatted_address;
                const latitude = place.geometry.location.lat();
                const longitude = place.geometry.location.lng();

                setInputValue(address);
                setPlace(place);
                setLat(latitude);
                setLng(longitude);
            }
        } else {
            console.error("Autocomplete has not loaded yet");
        }
    }

    function handleInputChange(value) {
        setInputValue(value);

        if (place) {
            setPlace(null);
        }
    }

    function handleRadiusChange(e) {
        setRadius(e.target.value);
    }

    function handleSearch() {
        setCityZipClasses(inputValue ? "" : "border border-danger rounded");
        if (inputValue) {
            setDoSearch(true);
        }
    }

    return (
        <SearchRow className="px-4 pt-1 align-center justify-content-center row">
            <SearchColumn className="mr-3 mb-2 mb-lg-0 col-12 col-lg-auto p-0">
                <div className="input-group">
                    <div className="input-group-prepend flex-grow-1">
                        <APIProvider apiKey={googleConfig.apiKey} language={lang} libraries={googleConfig.libraries}>
                            <GeoSearch
                                focusOnInput={focusOnInput}
                                value={inputValue || ''}
                                label="Zip code or city"
                                types={["(regions)"]}
                                restrictions={{ country: "us" }}
                                loadMap={loadMap}
                                handleLoad={handleLoad}
                                handleInputChange={handleInputChange}
                                onEnter={handleSearch}
                                handlePlaceChanged={handlePlaceChanged}
                                className={cityZipClasses}
                                dataTestId={createDataTestId({feature: AutomationTestIdFeature.WEALTH_ADVISOR_LOOKUP, element: AutomationTestIdElementType.INPUT, specifier: 'search_zip_city'})} />
                        </APIProvider>
                    </div>
                    <div className="input-group-append d-lg-none">
                        <Button className="search-button btn-primary px-2 py-1 rounded-right" onClick={handleSearch}>
                            <FontAwesomeIcon icon={faSearch} size="lg" />
                        </Button>
                    </div>
                </div>
            </SearchColumn>
            <SearchColumn className="mr-3 col-md-auto p-0 d-none d-lg-block">
                <select onChange={handleRadiusChange} value={radius} className="border-0 form-control h-100" data-test-id={createDataTestId({feature: AutomationTestIdFeature.WEALTH_ADVISOR_LOOKUP, element: AutomationTestIdElementType.SELECT, specifier: 'search_range'})}>
                    <option value="5">Within 5 miles</option>
                    <option value="10">Within 10 Miles</option>
                    <option value="25">Within 25 Miles</option>
                    <option value="50">Any distance</option>
                </select>
            </SearchColumn>
            <div className="mt-2 mt-md-0 d-none d-lg-block">
                <Button type="button"
                    className="h-100 d-flex align-items-center"
                    data-test-id={createDataTestId({feature: AutomationTestIdFeature.WEALTH_ADVISOR_LOOKUP, element: AutomationTestIdElementType.BUTTON, specifier: 'search_submit'})}
                    onClick={handleSearch}>Find an advisor</Button>
            </div>
        </SearchRow>
    );
};